import React from "react";
import Skeleton from "react-loading-skeleton";
import { Spinner } from "@material-tailwind/react";
import "react-loading-skeleton/dist/skeleton.css";

/**
 * DataTable — the canonical table primitive used across the FE.
 *
 * Single source of truth for our table styling so every list screen reads
 * the same: rounded-2xl card shell, sticky slate-50 header with
 * uppercase/tracking-wider labels, hover rows, horizontal scroll inside the
 * card on narrow screens (columns never collapse), and built-in skeleton,
 * empty, and refetch states.
 *
 * The columns prop is a small spec — each column gives a header label, a
 * width (px), an alignment, and either an `accessor` (string field name) or
 * a `render(row, index)` function for custom cells.
 *
 * Example:
 *   <DataTable
 *     data={rows}
 *     loading={loading}
 *     emptyState={<MyEmpty />}
 *     minWidth={1180}
 *     rowKey="id"
 *     onRowClick={(row) => navigate(`/x/${row.id}`)}
 *     columns={[
 *       { key: 'name', header: 'Name', width: 240, accessor: 'name' },
 *       { key: 'status', header: 'Status', width: 100, render: (r) => <StatusPill v={r.status} /> },
 *     ]}
 *   />
 */
function DataTable({
  columns = [],
  data = [],
  loading = false,
  refetching = false,
  emptyState = null,
  rowKey = "id",
  onRowClick,
  minWidth = 900,
  skeletonRows = 8,
  className = "",
  // Optional content rendered above the table inside the same card — usually
  // a filter strip. Kept as a slot so consumers can provide row-of-controls
  // without breaking the card chrome.
  toolbar = null,
}) {
  const getRowKey = (row, idx) =>
    typeof rowKey === "function" ? rowKey(row, idx) : row?.[rowKey] ?? idx;

  const alignClass = (a) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  // --- Loading skeleton (first paint only — the refetching overlay handles
  // subsequent loads so existing data stays visible while a new page lands).
  if (loading) {
    return (
      <div
        className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${className}`}
      >
        {toolbar && (
          <div className="px-4 py-3 border-b border-slate-200">{toolbar}</div>
        )}
        <div className="p-5 space-y-3">
          <Skeleton height={40} />
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <Skeleton key={i} height={48} />
          ))}
        </div>
      </div>
    );
  }

  // --- Empty state.
  if (!data || data.length === 0) {
    return (
      <div
        className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${className}`}
      >
        {toolbar && (
          <div className="px-4 py-3 border-b border-slate-200">{toolbar}</div>
        )}
        {emptyState || (
          <div className="px-6 py-16 text-center text-sm text-slate-500">
            No records to display.
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative ${className}`}
    >
      {/* Refetch overlay — backdrop-blur over the table only, so the user
          keeps row context while a page change loads. */}
      {refetching && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-20 flex items-center justify-center">
          <Spinner color="orange" size="lg" />
        </div>
      )}

      {toolbar && (
        <div className="px-4 py-3 border-b border-slate-200">{toolbar}</div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: `${minWidth}px` }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={c.width ? { width: `${c.width}px` } : undefined} />
            ))}
          </colgroup>

          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr className="text-[11px] uppercase tracking-wider text-slate-500">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 font-semibold ${alignClass(c.align)} ${c.headerClassName || ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {data.map((row, idx) => {
              const interactive = typeof onRowClick === "function";
              return (
                <tr
                  key={getRowKey(row, idx)}
                  onClick={interactive ? () => onRowClick(row, idx) : undefined}
                  className={`transition-colors ${
                    interactive
                      ? "cursor-pointer hover:bg-slate-50/60"
                      : "hover:bg-slate-50/60"
                  }`}
                >
                  {columns.map((c) => {
                    const value = c.render
                      ? c.render(row, idx)
                      : c.accessor
                      ? row?.[c.accessor]
                      : null;
                    return (
                      <td
                        key={c.key}
                        className={`px-4 py-3 align-middle ${alignClass(c.align)} ${c.cellClassName || ""}`}
                      >
                        {value ?? <span className="text-slate-400">—</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;
